import { motion } from "framer-motion";
import {
  FaCode,
  FaRocket,
  FaLaptopCode,
  FaCheckCircle,
} from "react-icons/fa";
import SectionTitle from "../ui/SectionTitle";

const highlights = [
  "10+ Years of Professional Experience",
  "1000+ Websites Successfully Delivered",
  "React & Next.js Specialist",
  "WordPress & WooCommerce Expert",
  "SEO & Core Web Vitals Optimized",
  "Clean, Maintainable Code",
];

const stats = [
  {
    value: "10+",
    label: "Years Experience",
  },
  {
    value: "1000+",
    label: "Projects Completed",
  },
  {
    value: "200+",
    label: "Happy Clients",
  },
  {
    value: "25+",
    label: "Countries Served",
  },
];

const services = [
  {
    icon: <FaLaptopCode />,
    title: "Frontend Development",
    text: "Modern, responsive interfaces using React, Next.js and Tailwind CSS.",
  },
  {
    icon: <FaRocket />,
    title: "Performance",
    text: "Fast-loading websites with excellent Core Web Vitals and SEO.",
  },
  {
    icon: <FaCode />,
    title: "Scalable Solutions",
    text: "Reusable architecture for business websites and web applications.",
  },
];

export default function About() {
  return (
    <section
      id="about"
      className="section relative overflow-hidden bg-[#07101f]"
    >
      <div className="absolute -left-32 top-20 h-80 w-80 rounded-full bg-blue-600/10 blur-[140px]" />
      <div className="absolute right-0 bottom-0 h-96 w-96 rounded-full bg-cyan-500/10 blur-[150px]" />

      <div className="container">

        <SectionTitle
          subtitle="About Me"
          title="Creating High-Performance Digital Experiences"
        />

        <div className="grid gap-16 lg:grid-cols-2 items-start">

          {/* Left */}

          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: .6 }}
            viewport={{ once: true }}
          >

            <h3 className="text-4xl lg:text-5xl font-bold leading-tight">

              Senior Frontend Developer with
              <span className="text-blue-500"> 10+ Years </span>
              of Experience.

            </h3>

            <p className="mt-8 text-lg leading-8 text-slate-400">

              I help startups, agencies and businesses transform ideas
              into fast, scalable and visually engaging web experiences.
              My expertise spans React, Next.js, WordPress,
              Tailwind CSS and modern frontend architecture.

            </p>

            <p className="mt-6 text-lg leading-8 text-slate-400">

              Over the past decade I have delivered more than
              <span className="text-white font-semibold"> 1000 successful websites </span>
              ranging from business websites and ecommerce platforms to
              enterprise applications, always focusing on performance,
              accessibility and exceptional user experience.

            </p>

            <div className="grid sm:grid-cols-2 gap-4 mt-10">

              {highlights.map((item) => (

                <div
                  key={item}
                  className="flex items-start gap-3"
                >
                  <FaCheckCircle className="text-blue-500 mt-1 shrink-0" />

                  <span className="text-slate-300">

                    {item}

                  </span>

                </div>

              ))}

            </div>

          </motion.div>

          {/* Right */}

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: .6 }}
            viewport={{ once: true }}
          >

            <div className="grid grid-cols-2 gap-6">

              {stats.map((item) => (

                <div
                  key={item.label}
                  className="rounded-3xl border border-white/10 bg-white/5 p-8 text-center backdrop-blur-lg"
                >

                  <h3 className="text-5xl font-black text-blue-500">

                    {item.value}

                  </h3>

                  <p className="mt-3 text-slate-400">

                    {item.label}

                  </p>

                </div>

              ))}

            </div>

            <div className="space-y-6 mt-10">

              {services.map((service) => (

                <div
                  key={service.title}
                  className="rounded-3xl border border-white/10 bg-white/5 p-8 flex gap-6 hover:border-blue-500 transition"
                >

                  <div className="h-16 w-16 rounded-2xl bg-blue-600 flex items-center justify-center text-3xl shrink-0">

                    {service.icon}

                  </div>

                  <div>

                    <h4 className="text-xl font-bold mb-3">

                      {service.title}

                    </h4>

                    <p className="text-slate-400 leading-7">

                      {service.text}

                    </p>

                  </div>

                </div>

              ))}

            </div>

          </motion.div>

        </div>

      </div>
    </section>
  );
}